import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Upload, Trash2, Loader2, Check, Film, ImagePlus, X, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { sendVideo, stageText } from "./videoSend/sendVideo";
import { readPrefs } from "./videoSend/prefs";
import { SendOptionsButton } from "./videoSend/SendOptions";

const CHUNK_SIZE = 4 * 1024 * 1024;
const isVideoFile = (f) => f.type.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/i.test(f.name);
const randomHex = () =>
  [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");

// Reusable per-event poster/flyer gallery. Admin + magician manage inline
// (drag-and-drop, video, ordering, moderation). Verified users can submit
// photos (20/day) that queue for approval.
// Usage: <PosterWall eventSlug="porchfest-2026" />
export const PosterWall = ({ eventSlug, emptyText = "Posters landing here soon — check back!" }) => {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "magician";
  const isVerifiedUser = !!user && !canManage && !user.is_anonymous && !!user.email && user.email_verified !== false;
  const [posters, setPosters] = useState([]);
  const [pending, setPending] = useState([]);
  const [progress, setProgress] = useState(null); // {done, total, label}
  const [dragOver, setDragOver] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);
  const zipRef = useRef(null);
  const userFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/event-posters/${eventSlug}`);
      setPosters(r.data.posters || []);
    } catch {
      /* wall just stays empty */
    }
    if (canManage) {
      try {
        const r = await axios.get(`${API}/admin/event-posters/${eventSlug}/pending`, { withCredentials: true });
        setPending(r.data.posters || []);
      } catch {
        /* ignore */
      }
    }
  }, [eventSlug, canManage]);

  useEffect(() => { load(); }, [load]);

  // Videos go through the shared sender: shrunk when they're too big for the wall, dropped uploads picked up, screen kept awake.
  const uploadVideo = (file, onStage) => sendVideo(file, { type: "poster", eventSlug }, readPrefs(), onStage);

  // Admin/magician: images + videos, unlimited
  const handleAdminFiles = async (files) => {
    if (!files.length) return;
    let ok = 0;
    for (let idx = 0; idx < files.length; idx++) {
      const file = files[idx];
      setProgress({ done: idx, total: files.length, label: file.name });
      try {
        if (isVideoFile(file)) {
          await uploadVideo(file, (s) =>
            setProgress({ done: idx + (s?.pct || 0), total: files.length, label: `${file.name} · ${stageText(s)}` })
          );
        } else {
          const form = new FormData();
          form.append("file", file);
          await axios.post(`${API}/admin/event-posters/${eventSlug}`, form, { withCredentials: true });
        }
        ok++;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || err.message || "upload failed"}`);
      }
    }
    setProgress(null);
    if (ok) toast.success(`${ok} item${ok === 1 ? "" : "s"} added to the wall`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  // Verified users: images only, 20/day, queued for approval
  const handleUserFiles = async (files) => {
    if (!files.length) return;
    let ok = 0;
    for (let idx = 0; idx < files.length; idx++) {
      const file = files[idx];
      setProgress({ done: idx, total: files.length, label: file.name });
      try {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/event-posters/${eventSlug}/submit`, form, { withCredentials: true });
        ok++;
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || "upload failed"}`);
        if (err.response?.status === 429) break;
      }
    }
    setProgress(null);
    if (ok) toast.success(`${ok} photo${ok === 1 ? "" : "s"} sent for review — thanks for sharing!`);
    if (userFileRef.current) userFileRef.current.value = "";
  };

  const handleDelete = async (posterId, { silent = false } = {}) => {
    if (!silent && !window.confirm("Remove this from the wall?")) return;
    try {
      await axios.delete(`${API}/admin/event-posters/${posterId}`, { withCredentials: true });
      setPosters((p) => p.filter((x) => x.poster_id !== posterId));
      setPending((p) => p.filter((x) => x.poster_id !== posterId));
      if (!silent) toast.success("Removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleApprove = async (posterId) => {
    try {
      await axios.put(`${API}/admin/event-posters/${posterId}/approve`, {}, { withCredentials: true });
      toast.success("Approved — it's on the wall");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    }
  };

  const toggleSelect = (posterId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(posterId) ? next.delete(posterId) : next.add(posterId);
      return next;
    });
  };

  const deleteSelected = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} poster${selected.size === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/event-posters/${eventSlug}/bulk-delete`,
        { poster_ids: [...selected] },
        { withCredentials: true }
      );
      toast.success(`${r.data.deleted} poster${r.data.deleted === 1 ? "" : "s"} deleted`);
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const handleZipFile = async (file) => {
    if (!file) return;
    if (file.size > 250 * 1024 * 1024) return toast.error("ZIP over 250MB — split it up");
    const total = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = randomHex();
    try {
      for (let i = 0; i < total; i++) {
        const form = new FormData();
        form.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), file.name);
        form.append("upload_id", uploadId);
        form.append("chunk_index", i);
        form.append("total_chunks", total);
        setProgress({ done: i, total, label: `Uploading ${file.name}` });
        await axios.post(`${API}/admin/event-posters/${eventSlug}/zip-chunk`, form, { withCredentials: true });
      }
      toast.success("ZIP received! Posters are being added in the background — you'll get a bell notification, then refresh.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "ZIP upload failed");
    } finally {
      setProgress(null);
      if (zipRef.current) zipRef.current.value = "";
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = posters.findIndex((p) => p.poster_id === active.id);
    const newIndex = posters.findIndex((p) => p.poster_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(posters, oldIndex, newIndex);
    setPosters(next);
    try {
      await axios.put(
        `${API}/admin/event-posters/${eventSlug}/reorder`,
        { poster_ids: next.map((p) => p.poster_id) },
        { withCredentials: true }
      );
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const optimizeWall = async () => {
    try {
      await axios.post(`${API}/admin/event-posters/${eventSlug}/optimize`, null, { withCredentials: true });
      toast.success("Cleanup started — compressing images & removing broken entries. Check your bell in a minute, then refresh.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start cleanup");
    }
  };

  return (
    <div>
      {canManage && (
        <div className="mb-6" data-testid="poster-wall-admin-bar">
          {/* Drag-and-drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleAdminFiles(Array.from(e.dataTransfer.files || [])); }}
            onClick={() => !progress && fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              dragOver ? "border-[#E76F51] bg-[#E76F51]/10" : "border-[#E76F51]/40 bg-[#E76F51]/5 hover:bg-[#E76F51]/10"
            }`}
            data-testid="poster-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              multiple
              className="hidden"
              onChange={(e) => handleAdminFiles(Array.from(e.target.files || []))}
              data-testid="poster-upload-input"
            />
            <div className="flex items-center justify-center gap-2 text-[#E76F51] font-semibold text-sm mb-1">
              <Upload className="h-4 w-4" /> <span>Admin: drop photos &amp; videos here, or tap to select</span>
            </div>
            <p className="text-xs text-[#5C5C5C]">
              Select as many as you like — JPG / PNG / WebP / GIF up to 10MB · MP4 / MOV / WebM video (bigger ones are shrunk to fit 100MB)
            </p>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <SendOptionsButton className="!py-1 h-7" />
              <input ref={zipRef} type="file" accept=".zip" className="hidden" onChange={(e) => handleZipFile(e.target.files?.[0])} data-testid="poster-zip-input" />
              <Button size="sm" variant="outline" className="h-7 text-xs rounded-full border-[#E76F51]/50 text-[#E76F51]" onClick={() => zipRef.current?.click()} disabled={!!progress} data-testid="poster-zip-btn">
                <Upload className="h-3 w-3 mr-1" /> Import ZIP of photos
              </Button>
              {!selectMode ? (
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => setSelectMode(true)} data-testid="poster-select-mode-btn">
                  Select multiple
                </Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => setSelected(new Set(posters.map((p) => p.poster_id)))} data-testid="poster-select-all-btn">
                    Select all ({posters.length})
                  </Button>
                  <Button size="sm" className="h-7 text-xs rounded-full bg-red-600 hover:bg-red-700" onClick={deleteSelected} disabled={!selected.size} data-testid="poster-delete-selected-btn">
                    <Trash2 className="h-3 w-3 mr-1" /> Delete selected ({selected.size})
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full" onClick={() => { setSelectMode(false); setSelected(new Set()); }} data-testid="poster-select-cancel-btn">
                    Cancel
                  </Button>
                </>
              )}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); optimizeWall(); }}
              className="text-xs text-[#5C5C5C] underline hover:text-[#2E5C55]"
              data-testid="poster-optimize-btn"
            >
              Images loading slow or broken? Fix &amp; compress the wall
            </button>
          </div>

          {/* Progress bar */}
          {progress && (
            <div className="mt-3" data-testid="poster-upload-progress">
              <div className="flex justify-between text-xs text-[#5C5C5C] mb-1">
                <span className="truncate mr-2 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> {progress.label}
                </span>
                <span>{Math.min(Math.floor(progress.done), progress.total)} / {progress.total}</span>
              </div>
              <div className="h-2 bg-[#E2E0DC] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#E76F51] transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Pending approval queue */}
          {pending.length > 0 && (
            <div className="mt-4 border border-amber-300 bg-amber-50 rounded-xl p-4" data-testid="poster-pending-queue">
              <p className="text-sm font-bold text-amber-800 mb-3">
                {pending.length} community photo{pending.length === 1 ? "" : "s"} awaiting approval
              </p>
              <div className="flex flex-wrap gap-3">
                {pending.map((p) => (
                  <div key={p.poster_id} className="w-36 bg-white rounded-lg shadow overflow-hidden" data-testid={`pending-poster-${p.poster_id}`}>
                    <img src={`${API.replace(/\/api$/, "")}${p.image_url}`} alt="pending" className="w-full h-28 object-cover" loading="lazy" />
                    <p className="text-[10px] text-[#5C5C5C] px-1.5 pt-1 truncate">{p.uploaded_by_name || "Community"}</p>
                    <div className="flex gap-1 p-1.5">
                      <Button size="sm" className="h-7 flex-1 bg-[#2E5C55] hover:bg-[#1F3F3A]" onClick={() => handleApprove(p.poster_id)} data-testid={`pending-approve-${p.poster_id}`}>
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 flex-1 text-red-500 border-red-200" onClick={() => handleDelete(p.poster_id, { silent: true })} data-testid={`pending-reject-${p.poster_id}`}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Verified community members: share photos (20/day, reviewed) */}
      {isVerifiedUser && (
        <div className="mb-6 flex flex-wrap items-center gap-3 border border-[#2E5C55]/30 bg-[#2E5C55]/5 rounded-xl p-4" data-testid="poster-user-submit-bar">
          <input
            ref={userFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="hidden"
            onChange={(e) => handleUserFiles(Array.from(e.target.files || []))}
            data-testid="poster-user-upload-input"
          />
          <Button
            size="sm"
            onClick={() => userFileRef.current?.click()}
            disabled={!!progress}
            className="bg-[#2E5C55] hover:bg-[#1F3F3A] rounded-full"
            data-testid="poster-user-upload-btn"
          >
            {progress ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-2" />}
            {progress ? "Sending..." : "Share your photos"}
          </Button>
          <span className="text-xs text-[#5C5C5C]">
            Up to 20 photos a day — they go live once approved. Thanks for helping build the wall!
          </span>
        </div>
      )}

      {posters.length === 0 ? (
        <div className="border-2 border-dashed border-[#C9C6C0] rounded-xl p-12 text-center text-[#5C5C5C]" data-testid="poster-wall-empty">
          <Clock className="h-8 w-8 mx-auto mb-3 opacity-50" />
          {emptyText}
        </div>
      ) : canManage && !selectMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={posters.map((p) => p.poster_id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-start" data-testid="poster-wall-grid">
              {posters.map((poster) => (
                <SortablePosterCard
                  key={poster.poster_id}
                  poster={poster}
                  onDelete={() => handleDelete(poster.poster_id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [&>*]:mb-4" data-testid="poster-wall-grid">
          {posters.map((poster) => (
            <PosterCard
              key={poster.poster_id}
              poster={poster}
              isAdmin={canManage}
              onDelete={() => handleDelete(poster.poster_id)}
              selectMode={selectMode}
              isSelected={selected.has(poster.poster_id)}
              onToggleSelect={() => toggleSelect(poster.poster_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SortablePosterCard = ({ poster, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: poster.poster_id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, position: "relative" }}
      className={isDragging ? "opacity-90 shadow-2xl rounded-lg" : ""}
    >
      <PosterCard poster={poster} isAdmin dragHandleProps={{ ...attributes, ...listeners }} onDelete={onDelete} />
    </div>
  );
};

const PosterCard = ({ poster, isAdmin, onDelete, selectMode, isSelected, onToggleSelect, dragHandleProps }) => {
  const [caption, setCaption] = useState(poster.caption || "");
  const [saving, setSaving] = useState(false);
  const [broken, setBroken] = useState(false);
  const src = `${API.replace(/\/api$/, "")}${poster.image_url}`;

  // Broken file: hide from the public entirely; show admin a removable stub
  if (broken && !isAdmin) return null;

  const saveCaption = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      form.append("caption", caption);
      await axios.put(`${API}/admin/event-posters/${poster.poster_id}`, form, { withCredentials: true });
      toast.success("Caption saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <figure
      className={`break-inside-avoid rounded-lg overflow-hidden shadow-md bg-white relative group ${selectMode ? "cursor-pointer" : ""} ${isSelected ? "ring-4 ring-red-500" : ""}`}
      onClick={selectMode ? onToggleSelect : undefined}
      data-testid={selectMode ? `poster-selectable-${poster.poster_id}` : undefined}
    >
      {selectMode && (
        <span className={`absolute top-2 right-2 z-10 h-6 w-6 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-red-500 border-red-500 text-white" : "bg-white/90 border-[#C9C6C0]"}`}>
          {isSelected && <Check className="h-4 w-4" />}
        </span>
      )}
      {isAdmin && dragHandleProps && !broken && (
        <div
          {...dragHandleProps}
          className="flex items-center justify-center gap-1 py-1.5 bg-[#F0EEE9] text-[#5C5C5C] cursor-grab active:cursor-grabbing touch-none select-none hover:bg-[#E7E4DE] transition-colors"
          data-testid={`poster-drag-handle-${poster.poster_id}`}
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
          <span className="text-[10px] font-semibold uppercase tracking-wide">Drag to reorder</span>
        </div>
      )}
      {broken ? (
        <div className="p-4 bg-red-50 text-center" data-testid={`poster-broken-${poster.poster_id}`}>
          <p className="text-xs text-red-600 font-semibold mb-2">Image failed to load — remove it?</p>
          <Button size="sm" variant="outline" className="h-7 text-red-500 border-red-200" onClick={onDelete}>
            <Trash2 className="h-3 w-3 mr-1" /> Remove
          </Button>
        </div>
      ) : poster.media_type === "video" ? (
        <div className="relative">
          <video src={src} controls playsInline preload="metadata" className="w-full" data-testid={`poster-video-${poster.poster_id}`} />
          <span className="absolute top-2 left-2 bg-black/60 text-white rounded-full p-1 pointer-events-none">
            <Film className="h-3 w-3" />
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt={poster.caption || "Event poster"}
          className="w-full"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      )}
      {broken ? null : isAdmin ? (
        <div className="p-2 space-y-1.5">
          <div className="flex gap-1.5">
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Band / caption"
              className="h-8 text-xs"
              maxLength={200}
            />
            <Button size="sm" variant="outline" className="h-8 px-2" onClick={saveCaption} disabled={saving}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </Button>
            <Button size="sm" variant="outline" className="h-8 px-2 text-red-500 border-red-200" onClick={onDelete} data-testid={`poster-delete-${poster.poster_id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ) : (
        poster.caption && (
          <figcaption className="text-xs text-[#5C5C5C] p-2">{poster.caption}</figcaption>
        )
      )}
    </figure>
  );
};

export default PosterWall;
